import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Car, Mail, Phone, Search, X, ChevronRight, ChevronLeft, RefreshCw, ArrowUpDown, Calendar } from 'lucide-react';
import { formatZonedDateTime, formatTimeZoneLabel } from '../services/dateService';

const LeadsTable = ({
unreadLeadIds,
  leads,
  onLeadSelect,
  selectedLeadIds = [],
  page,
  size,
  totalPages,
  bodyTypeFilter,
  searchQuery = '',
  startDate,
  endDate,
  onPageChange,
  onPageSizeChange,
  onBodyTypeFilterChange,
  onSearchQueryChange = () => {},
  onDateRangeChange,
  onRefresh = () => {},
  isLoading = false,
  storeTimeZone = null,
  showStoreColumn = false,
  storeFilterOptions = [],
  selectedStoreFilter = null,
  onStoreFilterChange = null
}) => {

  // Title-cases whatever casing the backend sends ('new', 'PENDING', ...) so every
  // status reads the same way regardless of which field it came from.
  const titleCase = (value) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-amber-100 text-amber-800';
      case 'qualified':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getAppointmentStatusColor = (status) => {
    switch ((status || '').toUpperCase()) {
      case 'PENDING':
        return 'bg-amber-100 text-amber-800';
      case 'BOOKED':
        return 'bg-green-100 text-green-800';
      case 'RESCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIndicator = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-500';
      case 'medium':
        return 'bg-orange-500';
      case 'low':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const formatDate = (dateString, rowTimeZone) => formatZonedDateTime(dateString, rowTimeZone || storeTimeZone);

  // +14074900265 -> (407) 490-0265. Anything that isn't a 10-digit US number is shown as stored.
  const formatPhone = (phoneNumber) => {
    if (!phoneNumber) return '';
    let digits = phoneNumber.replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length !== 10) return phoneNumber;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleLeadClick = useCallback((lead) => {
    onLeadSelect(lead);
  }, [onLeadSelect]);

  const handleSearchChange = useCallback((e) => {
    onSearchQueryChange(e.target.value);
  }, [onSearchQueryChange]);

  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === 'NumpadEnter') {
      e.preventDefault();
      e.stopPropagation();
      onRefresh();
    }
  }, [onRefresh]);

  const handleClearSearch = useCallback(() => {
    onSearchQueryChange('');
    onRefresh();
  }, [onSearchQueryChange, onRefresh]);

  const handleStartDateChange = useCallback((e) => {
    onDateRangeChange(e.target.value, endDate);
  }, [endDate, onDateRangeChange]);

  const handleEndDateChange = useCallback((e) => {
    onDateRangeChange(startDate, e.target.value);
  }, [startDate, onDateRangeChange]);

  const handleBodyTypeFilterChange = useCallback((e) => {
    onBodyTypeFilterChange(e.target.value);
  }, [onBodyTypeFilterChange]);

  const handlePageSizeChange = useCallback((e) => {
    onPageSizeChange(Number(e.target.value));
  }, [onPageSizeChange]);

  const selectedStoreTimeZone = useMemo(() => {
    if (!selectedStoreFilter) return null;
    const match = storeFilterOptions.find((store) => store.id === selectedStoreFilter);
    return match?.timeZone || null;
  }, [selectedStoreFilter, storeFilterOptions]);

  return (
    <div>
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Search leads..."
            value={searchQuery || ''}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="w-full pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-400" />
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Start date"
          />
          <span className="text-gray-400">-</span>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="End date"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={bodyTypeFilter}
            onChange={handleBodyTypeFilterChange}
            className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          >
            <option value="">All Lead Sources</option>
            <option value="SCHEDULE">Schedule</option>
            <option value="SERVICE">Service</option>
            <option value="OTHER">Other</option>
          </select>
        </div>

        {storeFilterOptions.length > 0 && (
          <div className="flex items-center gap-2">
            <select
              value={selectedStoreFilter || ''}
              onChange={(e) => onStoreFilterChange && onStoreFilterChange(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Stores</option>
              {storeFilterOptions.map((store) => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
            {selectedStoreTimeZone && (
              <span
                className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 whitespace-nowrap"
                title="Timezone for the selected store"
              >
                {formatTimeZoneLabel(selectedStoreTimeZone)}
              </span>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-1.5 transition-colors ${
            isLoading
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {isLoading ? (
            <>
              <RefreshCw size={16} className="animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search size={16} />
              Search
            </>
          )}
        </button>
      </div>

      {/* Leads Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative min-h-[160px]">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b border-gray-200">
              <tr>
                {[
                  'Customer', 'Vehicle', 'Contact',
                  ...(showStoreColumn ? ['Store'] : []),
                  'Lead Source', 'Status', 'Created On', 'Last Received', ''
                ].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={showStoreColumn ? 9 : 8} className="px-4 py-16 text-center text-gray-400">
                    {searchQuery ? 'No leads found matching your search' : 'No leads found'}
                  </td>
                </tr>
              ) : (
                leads.map((lead) => (
                  <tr
                    key={lead.id}
                    onClick={() => handleLeadClick(lead)}
                    className={`group cursor-pointer transition-colors hover:bg-gray-50 ${
                      selectedLeadIds.includes(lead.id) ? 'bg-purple-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <span
                          className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${getPriorityIndicator(lead.priority)}`}
                        />
                        <span className={`text-gray-900 whitespace-nowrap ${unreadLeadIds && unreadLeadIds.has(lead.id) ? 'font-bold' : 'font-medium'}`}>
                          {lead.customerName}
                        </span>
                        {unreadLeadIds && unreadLeadIds.has(lead.id) && (
                          <span
                            className="ml-2 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 animate-pulse"
                            title="New message"
                          />
                        )}
                        {lead.interactionType === 'OUTBOUND_APPOINTMENT' && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800 whitespace-nowrap">
                            Outbound
                          </span>
                        )}
                        {lead.leadCategory && (
                          <span
                            className="ml-2 px-2 py-0.5 text-xs font-medium rounded bg-indigo-100 text-indigo-800 whitespace-nowrap"
                            title={lead.leadCategory.name || lead.leadCategory.code}
                          >
                            {lead.leadCategory.code}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div className="flex items-center">
                        <Car size={14} className="mr-1.5 flex-shrink-0 text-gray-400" />
                        <span className="whitespace-nowrap">
                          {lead.vehicle ? `${lead.vehicle.year} ${lead.vehicle.make} ${lead.vehicle.model}` : 'No vehicle info'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {lead.customerEmail && (
                        <div className="flex items-center text-xs">
                          <Mail size={12} className="mr-1 flex-shrink-0" />
                          <span className="truncate max-w-[140px]">{lead.customerEmail}</span>
                        </div>
                      )}
                      {lead.customerPhone && (
                        <div className={`flex items-center text-xs ${lead.customerEmail ? 'mt-1' : ''}`}>
                          <Phone size={12} className="mr-1 flex-shrink-0" />
                          <span className="whitespace-nowrap">{formatPhone(lead.customerPhone)}</span>
                        </div>
                      )}
                    </td>
                    {showStoreColumn && (
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        <div className="font-medium text-gray-900 whitespace-nowrap">{lead.storeName || lead.storeExternalId || '-'}</div>
                        {lead.timeZone && (
                          <div className="text-gray-500 whitespace-nowrap">{formatTimeZoneLabel(lead.timeZone)}</div>
                        )}
                        {(lead.assistantFirstName || lead.assistantLastName || lead.assistantPhoneNumber) && (
                          <div className="text-gray-500 whitespace-nowrap">
                            {[lead.assistantFirstName, lead.assistantLastName].filter(Boolean).join(' ')}
                            {lead.assistantPhoneNumber ? ` · ${lead.assistantPhoneNumber}` : ''}
                          </div>
                        )}
                      </td>
                    )}
                    <td className="px-4 py-3 text-xs">
                      <span className={`truncate max-w-[180px] inline-block ${lead.bodyText ? 'text-gray-600' : 'text-gray-400'}`} title={lead.bodyText}>
                        {lead.bodyText || '\u2014'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {lead.appointment ? (
                        <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${getAppointmentStatusColor(lead.appointment.status)}`}>
                          {titleCase(lead.appointment.status)}
                        </span>
                      ) : (
                        <span className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${getStatusColor(lead.status)}`}>
                          {titleCase(lead.status)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(lead.createdAt, lead.timeZone)}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(lead.lastReceivedMessageAt, lead.timeZone)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight
                        size={16}
                        className={
                          selectedLeadIds.includes(lead.id)
                            ? 'text-purple-500'
                            : 'text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity'
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <span className="mt-2 text-sm text-gray-600">Loading leads...</span>
          </div>
        </div>
      )}
      </div>

      {/* Pagination */}
      {totalPages > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
          <div className="flex items-center gap-3">
            <div className="text-sm text-gray-500 whitespace-nowrap">
              Page {page + 1} of {totalPages}
            </div>
            <select
              value={size}
              onChange={handlePageSizeChange}
              className="px-2 py-1.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
          <div className="flex items-center gap-2 max-w-full overflow-x-auto">
            <button
              onClick={() => onPageChange(page - 1)}
              disabled={page === 0}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i).map((p) => (
              <button
                key={p}
                onClick={() => onPageChange(p)}
                className={`px-3 py-2 border rounded-lg text-sm ${
                  p === page
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'border-gray-300 hover:bg-gray-50'
                }`}
              >
                {p + 1}
              </button>
            ))}
            <button
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages - 1}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(LeadsTable);
